/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import { memo, ReactNode, useMemo } from 'react'

import Card from '@mui/material/Card'
import Icon from '@mui/material/Icon'
import { Bar } from 'react-chartjs-2'

import colors from 'assets/theme/base/colors'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import configs from 'modules/Charts/BarCharts/HorizontalBarChart/configs'
import { ColorVariants } from 'types/mui-theme'

// Declaring props types for HorizontalBarChart
interface Props {
	icon?: {
		color?: ColorVariants
		component: ReactNode
	}
	title?: string
	description?: string | ReactNode
	height?: string | number
	chart?: {
		labels: string[]
		datasets: {
			label: string
			color: ColorVariants
			data: number[]
		}[]
	} | null
	[key: string]: any
}

function HorizontalBarChart({ icon, title, description, height, chart }: Props): JSX.Element {
	const isChartValid = chart && chart.datasets && chart.datasets.length > 0

	const chartDatasets = isChartValid
		? chart.datasets.map(dataset => ({
				...dataset,
				weight: 5,
				borderWidth: 0,
				borderRadius: 4,
				backgroundColor: colors[dataset.color]
					? colors[dataset.color || 'dark'].main
					: colors.dark.main,
				fill: false,
				maxBarThickness: 35,
			}))
		: []

	const { data, options } = isChartValid
		? configs(chart.labels || [], chartDatasets)
		: { data: null, options: null }

	const chartRender = useMemo(
		() =>
			isChartValid ? (
				<MDBox height={height}>
					<Bar data={data} options={options} />
				</MDBox>
			) : (
				<MDBox />
			),
		[height, isChartValid, data, options]
	)

	const renderChart = (
		<MDBox py={2} pr={2} pl={icon.component ? 1 : 2}>
			{title || description ? (
				<MDBox display="flex" px={description ? 1 : 0} pt={description ? 1 : 0}>
					{icon.component && (
						<MDBox
							width="4rem"
							height="4rem"
							bgColor={icon.color || 'info'}
							variant="gradient"
							coloredShadow={icon.color || 'info'}
							borderRadius="xl"
							display="flex"
							justifyContent="center"
							alignItems="center"
							color="white"
							mt={-5}
							mr={2}
						>
							<Icon fontSize="medium">{icon.component}</Icon>
						</MDBox>
					)}
					<MDBox mt={icon.component ? -2 : 1}>
						{title && <MDTypography variant="h6">{title}</MDTypography>}
						<MDBox mb={2}>
							<MDTypography component="div" variant="button" color="text">
								{description}
							</MDTypography>
						</MDBox>
					</MDBox>
				</MDBox>
			) : null}
			{chartRender}
		</MDBox>
	)

	return title || description ? <Card>{renderChart}</Card> : renderChart
}

// Declaring default props HorizontalBarChart
HorizontalBarChart.defaultProps = {
	icon: { color: 'info', component: '' },
	title: '',
	description: '',
	height: '19.125rem',
}

export default memo(HorizontalBarChart)
